// AI-CONTEXT: See docs/features/msi-onderbouwing-transparency.md
import { useEffect } from 'react'
import Modal from '../common/Modal'
import Table from '../common/table/Table'
import TableCell from '../common/table/TableCell'
import BronLink from './BronLink'
import formatEuroSigned from '../../helpers/formatEuroSigned'
import checkOnderbouwingConsistency from '../../helpers/msiMonetisationHelpers/checkOnderbouwingConsistency'

const formatNumber = (value) =>
    new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 2 }).format(value)

const signClass = (value) => (value > 0 ? 'pos' : value < 0 ? 'neg' : 'neutral')

// component/stakeholder are open enums: readable label from the raw string,
// never a hard fail on unknown values (e.g. 'zorgEnVeiligheid' → 'Zorg en
// veiligheid').
const humanizeEnum = (value) => {
    if (typeof value !== 'string' || !value) return ''
    const spaced = value.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()
    return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

// Neutral expectation-setting per effect status — never error styling, never
// hiding the effect.
const STATUS_NOTICES = {
    concept: 'Gedetailleerde financiële opbouw volgt in de definitieve versie 1.0.',
    gedeeltelijk: 'Voor een deel van de niveaus is de gedetailleerde financiële opbouw nog in bewerking.',
}

const OPBOUW_COLUMNS = [
    { key: 'component', label: 'Onderdeel' },
    { key: 'stakeholder', label: 'Voor wie' },
    { key: 'bedrag', label: 'Bedrag per jaar' },
    { key: 'berekening', label: 'Berekening' },
]

const PRIJSBASIS_COLUMNS = [
    { key: 'label', label: 'Tarief' },
    { key: 'waarde', label: 'Bedrag' },
    { key: 'eenheid', label: 'Eenheid' },
    { key: 'bronnen', label: 'Bron' },
]

/**
 * One collapsible score level: the situatie, the yearly value relative to the
 * nullijn, the financial opbouw (when authored), the literatuur prose and the
 * level-wide bronnen.
 *
 * @param {object} props
 * @param {import('../../helpers/msiMonetisationHelpers/buildOnderbouwingIndex').OnderbouwingNiveau} props.niveau - The score level
 */
const NiveauBlock = ({ niveau }) => {
    // A post without an amount (bedrag null) is listed but not summed.
    const opbouwSum = Array.isArray(niveau.opbouw)
        ? niveau.opbouw.reduce((sum, component) => sum + (component.bedrag ?? 0), 0)
        : null

    return (
        <details className='msi-onderbouwing-niveau'>
            <summary className='msi-onderbouwing-niveau-summary'>
                <span className='msi-onderbouwing-niveau-score'>Score {niveau.score}</span>
                {niveau.waardePerJaar === null ? (
                    <span className='msi-onderbouwing-niveau-waarde neutral'>Geen bedrag</span>
                ) : (
                    <span className={`msi-onderbouwing-niveau-waarde ${signClass(niveau.waardePerJaar)}`}>
                        {formatEuroSigned(niveau.waardePerJaar)} per persoon per jaar
                    </span>
                )}
                {niveau.waardePerJaar === 0 && (
                    <span className='msi-onderbouwing-nullijn'>nullijn — het landelijk gemiddelde</span>
                )}
            </summary>

            <p className='msi-onderbouwing-situatie'>{niveau.situatie}</p>

            {Array.isArray(niveau.opbouw) && (
                <div className='msi-onderbouwing-table-scroll'>
                    <Table
                        columns={OPBOUW_COLUMNS}
                        items={niveau.opbouw.map((component, index) => ({ index, ...component }))}
                        getItemKey={(item) => `${item.component}-${item.index}`}
                        showSearch={false}
                        renderRow={(item) => (
                            <>
                                {/* Free-text labels (versioned document) as written; enum codes humanized. */}
                                <TableCell>{item.label || humanizeEnum(item.component)}</TableCell>
                                <TableCell>{item.stakeholderLabel || humanizeEnum(item.stakeholder)}</TableCell>
                                <TableCell className={signClass(item.bedrag)}>
                                    {item.bedrag === null ? '—' : formatEuroSigned(item.bedrag)}
                                </TableCell>
                                <TableCell>
                                    <div className='msi-onderbouwing-berekening'>
                                        <span>{item.berekening}</span>
                                        {item.aannames.length > 0 && (
                                            <ul className='msi-onderbouwing-aannames'>
                                                {item.aannames.map((aanname) => (
                                                    <li key={aanname}>{aanname}</li>
                                                ))}
                                            </ul>
                                        )}
                                        {item.bronnen.length > 0 && (
                                            <div className='msi-onderbouwing-bron-list'>
                                                {item.bronnen.map((bron) => (
                                                    <BronLink key={bron.id} bron={bron} />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </TableCell>
                            </>
                        )}
                    />
                    <div className='msi-onderbouwing-som'>
                        <span>Som van de opbouw</span>
                        <span className={signClass(opbouwSum)}>{formatEuroSigned(opbouwSum)}</span>
                    </div>
                </div>
            )}

            {niveau.literatuur && (
                <div className='msi-onderbouwing-literatuur'>
                    <span className='msi-onderbouwing-subheading'>Literatuur</span>
                    <p>{niveau.literatuur}</p>
                </div>
            )}

            {niveau.bronnen.length > 0 && (
                <div className='msi-onderbouwing-bron-list'>
                    {niveau.bronnen.map((bron) => (
                        <BronLink key={bron.id} bron={bron} />
                    ))}
                </div>
            )}
        </details>
    )
}

/**
 * Modal showing the full substantiation behind one MSI effect's monetary
 * values: methodiek, the financial opbouw per score level (with assumptions
 * and sources), the universal tariffs used and the literature. Open while
 * `entry` is set; closing clears it via `onClose`. Uses the shared Modal.jsx.
 *
 * The /database scores stay authoritative for every calculation — when the
 * document diverges we log a warning and keep rendering the app's values.
 *
 * For an effect from a versioned standaard the entry comes from that
 * document (useStandaardOnderbouwing), which is fetched when the modal opens:
 * meanwhile `loading` (or a failed `error`) keeps the modal open under
 * `effectName`.
 *
 * @param {object} props
 * @param {import('../../helpers/msiMonetisationHelpers/buildOnderbouwingIndex').OnderbouwingEffect|null} props.entry - Onderbouwing entry of the clicked effect, null when closed or still loading
 * @param {{toelichting?: string|null, version?: string, title?: string}|null} props.meta - Document meta (banner, title + versie)
 * @param {Array<{score: number, monetaryValue: number}>|null} props.msiScores - The scores the effect is valued at, for the consistency check
 * @param {boolean} [props.loading] - The entry is still being fetched
 * @param {Error|null} [props.error] - Fetching the entry failed
 * @param {string} [props.effectName] - Header while loading or failed
 * @param {() => void} props.onClose - Called to close the modal
 */
const MsiOnderbouwingModal = ({ entry, meta, msiScores, loading = false, error = null, effectName = '', onClose }) => {
    useEffect(() => {
        if (!entry || !msiScores) return
        const mismatches = checkOnderbouwingConsistency(entry.niveaus, msiScores)
        if (mismatches.length > 0) {
            console.warn(`MSI-onderbouwing '${entry.id}' wijkt af van de /database-bedragen`, mismatches)
        }
    }, [entry, msiScores])

    const description = 'Waar de maatschappelijke waarde van dit effect vandaan komt: de berekening, de aannames, de gehanteerde tarieven en de bronnen. Alle bedragen zijn per persoon per jaar, ten opzichte van het landelijk gemiddelde.'

    if (!entry && (loading || error)) {
        return (
            <Modal
                modalOpen
                setModalOpen={onClose}
                pageHeader={`Onderbouwing — ${effectName}`}
                description={description}
                content={error
                    ? <p className='msi-onderbouwing-status-notice'>De onderbouwing kon niet worden geladen: {error.message}</p>
                    : <p className='msi-onderbouwing-status-notice'>Onderbouwing laden…</p>}
                hideConfirm
                cancelLabel='Sluiten'
            />
        )
    }

    if (!entry) return null

    const statusNotice = STATUS_NOTICES[entry.status]

    const content = (
        <div className='msi-onderbouwing'>
            {meta?.toelichting && (
                <div className='msi-onderbouwing-banner'>{meta.toelichting}</div>
            )}

            {statusNotice && (
                <p className='msi-onderbouwing-status-notice'>{statusNotice}</p>
            )}

            {entry.methodiek && (
                <details className='msi-onderbouwing-methodiek'>
                    <summary className='msi-onderbouwing-subheading'>Methodiek</summary>
                    <p>{entry.methodiek}</p>
                </details>
            )}

            <div className='msi-onderbouwing-niveaus'>
                {/* Hoog → laag (5 bovenaan), zoals de ladder overal in de app —
                    de API levert 1..5 oplopend (feedback impact calculator, idee #90). */}
                {[...entry.niveaus].sort((a, b) => Number(b.score) - Number(a.score)).map((niveau) => (
                    <NiveauBlock key={niveau.score} niveau={niveau} />
                ))}
            </div>

            {entry.prijsbasis.length > 0 && (
                <div className='msi-onderbouwing-prijsbasis'>
                    <span className='msi-onderbouwing-subheading'>Gehanteerde tarieven (universele bronnen)</span>
                    <div className='msi-onderbouwing-table-scroll'>
                        <Table
                            columns={PRIJSBASIS_COLUMNS}
                            items={entry.prijsbasis}
                            getItemKey={(item) => item.label}
                            showSearch={false}
                            renderRow={(item) => (
                                <>
                                    <TableCell>{item.label}</TableCell>
                                    <TableCell>{formatNumber(item.waarde)}</TableCell>
                                    <TableCell>{item.eenheid}</TableCell>
                                    <TableCell>
                                        <div className='msi-onderbouwing-bron-list'>
                                            {item.bronnen.map((bron) => (
                                                <BronLink key={bron.id} bron={bron} />
                                            ))}
                                        </div>
                                    </TableCell>
                                </>
                            )}
                        />
                    </div>
                </div>
            )}

            {meta?.version && (
                <p className='msi-onderbouwing-versie'>{meta.title || 'Meetstandaard onderbouwing'}, versie {meta.version}</p>
            )}
        </div>
    )

    return (
        <Modal
            modalOpen={!!entry}
            setModalOpen={onClose}
            pageHeader={`Onderbouwing — ${entry.effect}`}
            description={description}
            content={content}
            hideConfirm
            cancelLabel='Sluiten'
        />
    )
}

export default MsiOnderbouwingModal
